import * as React from "react"
import { MdWarning } from "react-icons/md"
import {
  Modal,
  ModalCard,
  StyledModal,
  StyledModalBody,
  StyledModalActions,
  Button,
  StyledModalHeader,
  Spacer,
  ThemeCss,
} from "gatsby-interface"
import { ModalMessage } from "@modules/modal"
import { Text } from "./shared/Text"
import {
  ui as uiText,
  gatsbyCloudCdnIntegrationConfig as text,
} from "@modules/locales/default.js"
import { FormattedMessage, commonMessageTags } from "@modules/locales"
import { useTriggerErrorAlert } from "@modules/ui/components/ErrorAlert"
import { HostingDetails } from "@modules/graphql/types"

const domainsCss: ThemeCss = theme => ({
  listStyle: `none`,
  margin: 0,
  marginTop: theme.space[6],
  color: theme.colors.grey[60],
  fontWeight: theme.fontWeights.semiBold,
  fontSize: theme.fontSizes[1],

  li: {
    display: `flex`,
    alignItems: `center`,
    margin: `${theme.space[2]} 0`,
  },

  svg: {
    color: theme.colors.red[60],
    marginRight: theme.space[3],
  },
})

export type DisableCloudCdnFormProps = {
  siteName: string
  onDismiss: (afterSubmit: boolean) => void
  onDisable: () => void
  disableRequested: boolean
  hostingDetails?: HostingDetails
}

export function DisableCloudCdnForm({
  siteName,
  onDismiss,
  onDisable,
  disableRequested,
  hostingDetails,
}: DisableCloudCdnFormProps) {
  const [setError, errorAlert] = useTriggerErrorAlert()

  const handleDisable = async () => {
    try {
      onDisable()
      onDismiss(true)
    } catch (error) {
      setError(error)
    }
  }

  const domains = hostingDetails?.domains

  return (
    <Modal
      isOpen={disableRequested}
      aria-label={text.messages.doYouWantToTurnOffGatsbyCloudCDN}
      onDismiss={() => onDismiss(false)}
      type="error"
    >
      <ModalCard>
        <StyledModal variant="WARNING">
          <StyledModalHeader onCloseButtonClick={() => onDismiss(false)}>
            {text.headers.turnOff}
          </StyledModalHeader>

          <StyledModalBody>
            <ModalMessage>
              <Text>
                <FormattedMessage<"siteName", "strong">
                  message={
                    text.messages.areYouSureYouWantToTurnOffGatsbyCloudCDN
                  }
                  values={{ siteName }}
                  tags={commonMessageTags}
                />
              </Text>
              <Spacer size={5} />
              <Text size="S">
                {text.messages.thisWillRemoveAndMakeDomainsUnavailable}
              </Text>
              {domains && (
                <ul css={domainsCss}>
                  {domains.map(item => (
                    <li key={item.id}>
                      <MdWarning /> {item.id}
                    </li>
                  ))}
                </ul>
              )}
            </ModalMessage>

            {errorAlert}

            <StyledModalActions>
              <Button
                type="button"
                onClick={() => onDismiss(false)}
                variant="SECONDARY"
              >
                {uiText.actions.cancel}
              </Button>
              <Button
                data-testid="remove-cms-integration"
                type="button"
                onClick={handleDisable}
                tone="DANGER"
                variant="PRIMARY"
              >
                {uiText.actions.yesTurnOff}
              </Button>
            </StyledModalActions>
          </StyledModalBody>
        </StyledModal>
      </ModalCard>
    </Modal>
  )
}
